import { useStore } from "@nanostores/react";
import { ReactElement, useMemo } from "react";

import { WorkflowStatus } from "../api";
import IconAlert from "../assets/icons/alert.svg";
import IconCircleCheck from "../assets/icons/circle-check.svg";
import IconProgress from "../assets/icons/progress.svg";
import { $workflowsByGraphID } from "../stores/workflows.ts";
import { GraphEdgeStatusFilterType } from "../types/graph.ts";
import { Tabs } from "./Tabs.tsx";

type Props = {
  currentTab: GraphEdgeStatusFilterType;
  onClick: (tab: GraphEdgeStatusFilterType) => void;
};

export const GraphLogsTabs = ({
  currentTab,
  onClick,
}: Props): null | ReactElement => {
  const { data, loading } = useStore($workflowsByGraphID);

  const allQueryActionsStatuses: Array<{
    icon?: ReactElement;
    name: string;
    type: GraphEdgeStatusFilterType;
  }> = [
    {
      name: "All",
      type: "all",
    },
    {
      icon: <IconProgress className="h-4 w-4 animate-spin text-sky-500" />,
      name: "Pending",
      type: WorkflowStatus.RUNNING,
    },
    {
      icon: <IconCircleCheck className="h-4 w-4 text-emerald-500" />,
      name: "Success",
      type: WorkflowStatus.SUCCEEDED,
    },
    {
      icon: <IconAlert className="h-4 w-4 text-rose-500" />,
      name: "Failure",
      type: WorkflowStatus.FAILED,
    },
  ];

  const availableStatuses: GraphEdgeStatusFilterType[] =
    useMemo((): GraphEdgeStatusFilterType[] => {
      if (!data || loading) return ["all"];

      const statusSet = new Set(data.map((workflow) => workflow.status));
      const availableTypes: GraphEdgeStatusFilterType[] = [];

      allQueryActionsStatuses.forEach((status) => {
        if (status.type === "all" || statusSet.has(status.type)) {
          availableTypes.push(status.type);
        }
      });

      return availableTypes;
    }, [data, loading]);

  const filteredTabs = allQueryActionsStatuses.filter((tab) =>
    availableStatuses.includes(tab.type),
  );

  if (loading && !data) return null;

  return (
    <Tabs
      currentId={currentTab}
      items={filteredTabs.map((item) => ({
        id: item.type,
        name: (
          <span className="flex gap-x-2">
            {item.icon}
            {item.name}
          </span>
        ),
        onClick: (): void => {
          onClick(item.type);
        },
      }))}
    />
  );
};
